








































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { PropType } from "vue";
import moment from "moment";
import BaseLeafletMap, { IMapFocusRequest, IMapMarker, IMapPolyline, LatLngTuple } from "@/components/Map/BaseLeafletMap.vue";
import { IPraxisRoute, IPraxisRoutePoint, IPraxisSummary } from "@/interfaces/Student/IKiPilotPraxis";
import { getTaskResponseColor } from "@/constants/TaskResponse";

const FALLBACK_ROUTE_COLOR = "#2f6fd6";
const MEDIA_MARKER_COLOR = "#793A86";
const DEFAULT_CENTER: LatLngTuple = [54, 15]; // Europe, used when there's nothing to fit bounds to

// Solid polylines for ON_DRIVING track points; each ON_PAUSE run collapses to one straight dashed
// line from the point before the pause to where driving resumes (points in between dropped).
function buildRouteSegments(track: Array<IPraxisRoutePoint>): Array<{ points: Array<LatLngTuple>; dashed: boolean }> {
  const toTuple = (point: IPraxisRoutePoint): LatLngTuple => [point.lat, point.lon];
  const isPause = (point: IPraxisRoutePoint) => point.segmentType === "ON_PAUSE";

  const segments: Array<{ points: Array<LatLngTuple>; dashed: boolean }> = [];
  let run: Array<LatLngTuple> = [];
  let i = 0;

  while (i < track.length) {
    if (!isPause(track[i])) {
      run.push(toTuple(track[i]));
      i += 1;
      continue;
    }

    let j = i;
    while (j < track.length && isPause(track[j])) j += 1;

    const before = i > 0 ? track[i - 1] : track[i];
    const after = j < track.length ? track[j] : track[j - 1];

    if (run.length >= 2) segments.push({ points: run, dashed: false });
    run = [];
    segments.push({ points: [toTuple(before), toTuple(after)], dashed: true });

    if (j < track.length) {
      // Resume the next solid run from where driving picks back up, then skip past that
      // point so the main loop doesn't re-add it.
      run.push(toTuple(after));
      i = j + 1;
    } else {
      i = j;
    }
  }

  if (run.length >= 2) segments.push({ points: run, dashed: false });

  return segments;
}

@Component({ components: { BaseLeafletMap } })
export default class KiPilotPraxisMap extends Vue {
  @Prop({ type: Array as PropType<Array<IPraxisRoute>>, default: () => [] })
  public routes!: Array<IPraxisRoute>;

  @Prop({ type: Object as PropType<IPraxisSummary>, default: null })
  public summary!: IPraxisSummary | null;

  @Prop({ type: Number, default: null })
  public selectedRouteId!: number | null;

  @Prop({ type: Object as PropType<IMapFocusRequest | null>, default: null })
  public focusRequest!: IMapFocusRequest | null;

  public mapCenter: LatLngTuple = DEFAULT_CENTER;

  public get displayedRoutes(): Array<IPraxisRoute> {
    if (this.selectedRouteId) {
      return this.routes.filter((route) => route.routeId === this.selectedRouteId);
    }
    return this.routes;
  }

  public get displayedRoute(): IPraxisRoute | null {
    return this.selectedRouteId ? this.displayedRoutes[0] || null : null;
  }

  // Keeps the last selected route's data around after "Alle Fahrten" is clicked, so the date/type
  // badge stays mounted at its same size (just hidden via visibility) instead of collapsing and
  // shifting the button/panel next to it.
  public lastDisplayedRoute: IPraxisRoute | null = null;

  @Watch("displayedRoute", { immediate: true })
  public onDisplayedRouteChanged(route: IPraxisRoute | null): void {
    if (route) {
      this.lastDisplayedRoute = route;
    }
  }

  // Same reasoning as above, but for the very first render (and for switching education, which
  // swaps in a whole new routes array): before anything's ever been selected there's no "last"
  // route yet, so without this the badge would only gain its size on the first click. Falling
  // back to the first loaded route reserves that space from the start too.
  @Watch("routes", { immediate: true })
  public onRoutesChanged(routes: Array<IPraxisRoute>): void {
    if (!routes || routes.length === 0) return;

    const stillValid = this.lastDisplayedRoute && routes.some((route) => route.routeId === this.lastDisplayedRoute?.routeId);
    if (!stillValid) {
      this.lastDisplayedRoute = routes[0];
    }
  }

  public get polylines(): Array<IMapPolyline> {
    return this.displayedRoutes
      .filter((route) => route.track && route.track.length >= 2)
      .flatMap((route) => {
        const color = route.typeColor || FALLBACK_ROUTE_COLOR;
        const segments = buildRouteSegments(route.track);

        return segments.map((segment, index) => ({
          points: segment.points,
          color,
          dashed: segment.dashed,
          isRouteStart: index === 0,
          isRouteEnd: index === segments.length - 1,
        }));
      });
  }

  // Always sourced from every route (not just the displayed/selected one) so the historical
  // density backdrop stays put while the active route glows on top of it, Strava-style.
  public get heatPoints(): Array<LatLngTuple> {
    return this.routes
      .filter((route) => route.track && route.track.length > 0)
      .flatMap((route) => route.track.filter((point) => point.segmentType !== "ON_PAUSE").map((point) => [point.lat, point.lon] as LatLngTuple));
  }

  public get markers(): Array<IMapMarker> {
    const markers: Array<IMapMarker> = [];

    this.displayedRoutes.forEach((route) => {
      route.mediaLinks.forEach((mediaLink) => {
        markers.push({
          lat: mediaLink.lat,
          lon: mediaLink.lon,
          color: MEDIA_MARKER_COLOR,
          radius: 12,
          icon: "link",
          title: mediaLink.title,
          url: mediaLink.url,
        });
      });
      route.taskMarks.forEach((taskMark) => {
        if (taskMark.lat != null && taskMark.lon != null) {
          markers.push({
            lat: taskMark.lat,
            lon: taskMark.lon,
            color: getTaskResponseColor(taskMark.responseId) || "#999",
            radius: 12,
            icon: "traffic-cone",
            taskId: taskMark.taskId,
            markId: taskMark.id,
          });
        }
      });
    });

    return markers;
  }

  public onMarkerClick(marker: IMapMarker): void {
    if (marker.taskId != null) {
      this.$emit("select-task", marker.taskId, marker.markId);
    }
  }

  public get distanceKm(): string {
    const meters = this.displayedRoute ? this.displayedRoute.distanceInMeters : this.summary?.totalDistanceInMeters;
    return meters ? (meters / 1000).toFixed(1).replace(".", ",") : "0,0";
  }

  public get avgSpeed(): string {
    const kmh = this.displayedRoute ? this.displayedRoute.avgKMH : this.summary?.avgKMH;
    return kmh ? kmh.toFixed(0) : "0";
  }

  public formatDate(date?: string): string {
    return date ? moment(date).format("DD.MM.YYYY") : "";
  }
}
